import { type Tag } from '@hungryroot/rest';
import { type InteractionContentType } from '@hungryroot/segment';

// Tag IDs that mark a curated marketing item's media type. Every marketing
// item (and its serving-size variations) is assigned exactly one of these. The
// GIF tag is the authoritative signal that a video should render as a silent,
// looping ambient asset with no audio controls.
export const HOME_FEED_MEDIA_TYPE_TAG_IDS = {
  gif: 4970,
  video: 4971,
  editorialPhoto: 4972,
  contextPhoto: 4973,
} as const;

const MEDIA_TYPE_TAG_IDS = new Set<number>(
  Object.values(HOME_FEED_MEDIA_TYPE_TAG_IDS)
);

// The v3 pairings/products endpoints type `tags` as `Tag[]`, but return bare
// numeric tag IDs when called with `expand=tags`. Collect whichever shape the
// runtime hands us into a plain list of IDs.
export function resolveFeedMediaTagIds(
  tags: ReadonlyArray<number | Tag> | undefined
): number[] {
  if (tags === undefined) return [];

  const ids: number[] = [];
  for (const tag of tags) {
    if (typeof tag === 'number') {
      ids.push(tag);
    } else if (typeof tag.id === 'number') {
      ids.push(tag.id);
    }
  }

  return ids;
}

// Resolves whether an item's media should be treated as a GIF (silent loop,
// no audio controls) based on its media-type tag:
// - `true` — tagged GIF; never show audio controls.
// - `false` — tagged with another media type (Video / Editorial / Context);
//   audio controls follow the normal video rules.
// - `undefined` — no media-type tag; callers fall back to the aspect-ratio
//   heuristic.
export function resolveMediaTypeGifPreference(
  tags: ReadonlyArray<number | Tag> | undefined
): boolean | undefined {
  const ids = resolveFeedMediaTagIds(tags);

  if (ids.includes(HOME_FEED_MEDIA_TYPE_TAG_IDS.gif)) return true;
  if (ids.some((id) => MEDIA_TYPE_TAG_IDS.has(id))) return false;

  return undefined;
}

// Maps an item's media-type tag to the analytics `content_type`. The API tags
// are authoritative — this avoids guessing photo type from aspect ratio.
// Returns `undefined` when the item has no media-type tag, in which case
// callers fall back to the media/aspect-ratio heuristic.
export function resolveFeedMediaContentType(
  tags: ReadonlyArray<number | Tag> | undefined
): InteractionContentType | undefined {
  const ids = resolveFeedMediaTagIds(tags);

  if (ids.includes(HOME_FEED_MEDIA_TYPE_TAG_IDS.gif)) return 'gif';
  if (ids.includes(HOME_FEED_MEDIA_TYPE_TAG_IDS.video)) return 'video';
  if (ids.includes(HOME_FEED_MEDIA_TYPE_TAG_IDS.editorialPhoto)) {
    return 'editorial_photo';
  }
  // A "Context Photo" is a close-crop marketing shot with its own content type.
  // `standard_photo` is reserved for the untagged default PDP/RDP product image
  // (see the resolver's fallback), so Context Photo maps to `context_photo`.
  if (ids.includes(HOME_FEED_MEDIA_TYPE_TAG_IDS.contextPhoto)) {
    return 'context_photo';
  }

  return undefined;
}
